import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Truck, Plane, MapPin, ShieldCheck, Headphones, ShoppingBag } from "lucide-react";

const ICONS = [Truck, Plane, MapPin, ShieldCheck, Headphones, ShoppingBag];

const WhyChooseUs = () => {
  const { t } = useLang();

  return (
    <section id="why" data-testid="why-section" className="relative py-24 sm:py-32 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="h-px w-8 bg-brand-red" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-brand-red">
              {t.why.eyebrow}
            </span>
          </div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] text-brand-navy leading-[1.05]">
            {t.why.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.why.cards.map((c, i) => {
            const Icon = ICONS[i % ICONS.length];
            const isAccent = i === 1 || i === 4;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`why-card-${i}`}
                className={`group relative rounded-3xl p-7 sm:p-8 border transition-all duration-300 hover:-translate-y-1 ${
                  isAccent
                    ? "bg-brand-navy text-brand-cream border-brand-navy hover:shadow-[0_25px_50px_-15px_rgba(14,42,86,0.5)]"
                    : "bg-white text-brand-navy border-brand-navy/10 hover:shadow-[0_20px_40px_-15px_rgba(14,42,86,0.25)] hover:border-brand-navy/20"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${
                    isAccent ? "bg-brand-red text-white" : "bg-brand-cream text-brand-red"
                  }`}
                >
                  <Icon size={22} strokeWidth={2.2} />
                </div>
                <h3 className={`font-heading font-bold text-xl sm:text-[22px] tracking-tight leading-snug mb-3 ${
                  isAccent ? "text-brand-cream" : "text-brand-navy"
                }`}>
                  {c.t}
                </h3>
                <p className={`text-sm sm:text-base leading-relaxed ${
                  isAccent ? "text-brand-cream/85" : "text-brand-navy/75"
                }`}>
                  {c.d}
                </p>

                {/* Numbered marker */}
                <div className={`absolute top-6 right-7 font-heading font-black text-sm tracking-tight ${
                  isAccent ? "text-brand-cream/40" : "text-brand-navy/25"
                }`}>
                  0{i + 1}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
